import React, { useEffect, useState, useCallback } from 'react';
import { packingApi } from '../services/api';

export default function Packing() {
  const [queue, setQueue] = useState([]);
  const [stats, setStats] = useState({});
  const [idx, setIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [packing, setPacking] = useState(false);
  const [done, setDone] = useState(false);     // flash "Zabaleno!" feedback
  const [touchMode, setTouchMode] = useState(() => window.innerWidth < 1024);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([packingApi.queue(), packingApi.stats()])
      .then(([q, s]) => {
        // Paid first, then waiting
        const sorted = [...q].sort((a, b) => (b.is_paid ? 1 : 0) - (a.is_paid ? 1 : 0));
        setQueue(sorted);
        setStats(s);
        setIdx(0);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const current = queue[idx];
  const total = queue.length;

  async function handlePack() {
    if (!current || packing) return;
    setPacking(true);
    await packingApi.pack(current.id);
    setPacking(false);
    setDone(true);
    setTimeout(() => {
      setDone(false);
      // Move to next — reload queue to get fresh data
      load();
    }, 900);
  }

  function handleSkip() {
    if (idx < total - 1) setIdx(i => i + 1);
  }
  function handlePrev() {
    if (idx > 0) setIdx(i => i - 1);
  }

  // ── List mode (desktop) ──────────────────────────────────────────────────
  if (!touchMode) {
    return (
      <div>
        <div className="page-header">
          <h1 className="page-title">📫 Balírna</h1>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-primary" onClick={() => setTouchMode(true)}>📱 Dotykový režim</button>
            <button className="btn btn-secondary" onClick={load}>🔄 Obnovit</button>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
          {[
            { v: stats.to_pack, l: 'K zabalení', c: 'var(--green)' },
            { v: stats.waiting_payment, l: 'Čeká platbu', c: 'var(--orange)' },
            { v: stats.packed_today, l: 'Zabaleno dnes', c: 'var(--blue)' },
          ].map(s => (
            <div key={s.l} className="stat-card" style={{ borderTop: `3px solid ${s.c}`, minWidth: 130 }}>
              <div className="stat-value" style={{ color: s.c }}>{s.v ?? 0}</div>
              <div className="stat-label">{s.l}</div>
            </div>
          ))}
        </div>

        {loading && <div className="loading-center"><div className="spinner" /></div>}

        {!loading && queue.length === 0 && (
          <div className="empty-state">
            <div style={{ fontSize: 56, marginBottom: 12 }}>✅</div>
            <div style={{ fontSize: 20, fontWeight: 700 }}>Vše zabaleno!</div>
            <div style={{ color: 'var(--text-muted)', marginTop: 4 }}>Žádné objednávky k zabalení.</div>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
          {queue.map(o => (
            <PackCard key={o.id} order={o} onPack={load} />
          ))}
        </div>
      </div>
    );
  }

  // ── Touch / tablet mode ──────────────────────────────────────────────────
  return (
    <div style={{
      position: 'fixed', inset: 0,
      background: '#0d3a6e',
      display: 'flex', flexDirection: 'column',
      touchAction: 'manipulation',
      zIndex: 200,
    }}>
      {/* Top bar */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '12px 16px',
        background: 'rgba(0,0,0,0.2)',
        color: 'white',
        flexShrink: 0,
      }}>
        <button onClick={() => setTouchMode(false)} style={{
          background: 'rgba(255,255,255,0.15)', border: 'none', color: 'white',
          padding: '8px 14px', borderRadius: 8, fontSize: 14, cursor: 'pointer',
        }}>
          ← Desktop
        </button>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontWeight: 700, fontSize: 16 }}>📫 Balírna</div>
          <div style={{ fontSize: 12, opacity: 0.7 }}>
            {total === 0 ? 'Vše zabaleno' : `${idx + 1} / ${total}`}
          </div>
        </div>
        <button onClick={load} style={{
          background: 'rgba(255,255,255,0.15)', border: 'none', color: 'white',
          padding: '8px 14px', borderRadius: 8, fontSize: 14, cursor: 'pointer',
        }}>
          🔄
        </button>
      </div>

      {/* Progress bar */}
      {total > 0 && (
        <div style={{ height: 4, background: 'rgba(255,255,255,0.15)', flexShrink: 0 }}>
          <div style={{
            height: '100%',
            width: `${((idx + 1) / total) * 100}%`,
            background: '#4da6ff',
            transition: 'width 0.3s',
          }} />
        </div>
      )}

      {/* Main content */}
      <div style={{ flex: 1, overflow: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: 12 }}>

        {loading && (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div className="spinner" style={{ borderTopColor: 'white', borderColor: 'rgba(255,255,255,0.3)' }} />
          </div>
        )}

        {!loading && total === 0 && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'white' }}>
            <div style={{ fontSize: 80, marginBottom: 16 }}>✅</div>
            <div style={{ fontSize: 28, fontWeight: 700 }}>Vše zabaleno!</div>
            <div style={{ fontSize: 16, opacity: 0.7, marginTop: 8 }}>Žádné objednávky k zabalení.</div>
          </div>
        )}

        {!loading && current && (
          <>
            {/* Payment status banner */}
            <div style={{
              background: current.is_paid ? 'rgba(59,109,17,0.9)' : 'rgba(133,79,11,0.9)',
              color: 'white',
              borderRadius: 12,
              padding: '10px 16px',
              display: 'flex', alignItems: 'center', gap: 10,
              fontSize: 15, fontWeight: 700,
              flexShrink: 0,
            }}>
              <span style={{ fontSize: 22 }}>{current.is_paid ? '✅' : '⚠️'}</span>
              {current.is_paid ? 'ZAPLACENO — zabalit' : 'ČEKÁ NA PLATBU — upozornit zákazníka'}
            </div>

            {/* Order card */}
            <div style={{
              background: 'white',
              borderRadius: 16,
              padding: '20px',
              flexShrink: 0,
            }}>
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 22, fontWeight: 800, color: '#0d3a6e' }}>{current.customer_name}</div>
                <div style={{ fontSize: 13, color: '#666', marginTop: 2 }}>{current.order_number}</div>
              </div>

              {/* Delivery */}
              <div style={{
                background: current.delivery_method === 'post' ? '#e8f0fb' : '#edf7e3',
                borderRadius: 8, padding: '10px 14px', marginBottom: 14,
                display: 'flex', alignItems: 'center', gap: 10,
              }}>
                <span style={{ fontSize: 24 }}>{current.delivery_method === 'post' ? '📮' : '🏠'}</span>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15 }}>
                    {current.delivery_method === 'post' ? 'Zaslat poštou' : 'Vyzvednutí'}
                  </div>
                  {current.delivery_method === 'post' && current.customer_address && (
                    <div style={{ fontSize: 13, color: '#555', marginTop: 2 }}>
                      {current.customer_address}, {current.customer_zip} {current.customer_city}
                    </div>
                  )}
                </div>
              </div>

              {/* Items */}
              <div style={{ marginBottom: 4 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                  Položky k zabalení
                </div>
                {current.items?.map((item, i) => (
                  <div key={i} style={{
                    display: 'flex', alignItems: 'center',
                    padding: '10px 0',
                    borderBottom: i < current.items.length - 1 ? '1px solid #eee' : 'none',
                  }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: 8,
                      background: '#185FA5', color: 'white',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontWeight: 800, fontSize: 16, flexShrink: 0, marginRight: 12,
                    }}>{item.quantity}×</div>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 16 }}>{item.product_name}</div>
                      {item.variant && <div style={{ fontSize: 13, color: '#666' }}>{item.variant}</div>}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Contact info */}
            {(current.customer_phone || current.notes) && (
              <div style={{ background: 'rgba(255,255,255,0.1)', borderRadius: 12, padding: '14px 16px', color: 'white' }}>
                {current.customer_phone && (
                  <div style={{ fontSize: 15, marginBottom: current.notes ? 8 : 0 }}>
                    📱 <a href={`tel:${current.customer_phone}`} style={{ color: 'white' }}>{current.customer_phone}</a>
                  </div>
                )}
                {current.notes && (
                  <div style={{ fontSize: 14, opacity: 0.85 }}>💬 {current.notes}</div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Bottom action area */}
      {!loading && current && (
        <div style={{
          padding: '12px 16px 20px',
          background: 'rgba(0,0,0,0.2)',
          flexShrink: 0,
          display: 'flex', flexDirection: 'column', gap: 10,
        }}>

          {/* Main pack button */}
          <button
            onClick={handlePack}
            disabled={packing}
            style={{
              background: done ? '#3B6D11' : current.is_paid ? '#3B6D11' : '#854F0B',
              color: 'white',
              border: 'none',
              borderRadius: 16,
              padding: '22px',
              fontSize: 22,
              fontWeight: 800,
              cursor: packing ? 'not-allowed' : 'pointer',
              transition: 'all 0.15s',
              opacity: packing ? 0.7 : 1,
              transform: packing ? 'scale(0.97)' : 'scale(1)',
              boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
              letterSpacing: '0.02em',
            }}
          >
            {done ? '✅ Zabaleno!' : packing ? '⏳ Ukládám...' : '📦 ZABALENO — další'}
          </button>

          {/* Skip / prev navigation */}
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              onClick={handlePrev}
              disabled={idx === 0}
              style={{
                flex: 1, padding: '14px', borderRadius: 12, border: 'none',
                background: 'rgba(255,255,255,0.12)', color: 'white',
                fontSize: 16, fontWeight: 600, cursor: idx === 0 ? 'not-allowed' : 'pointer',
                opacity: idx === 0 ? 0.4 : 1,
              }}
            >
              ← Předchozí
            </button>
            <button
              onClick={handleSkip}
              disabled={idx >= total - 1}
              style={{
                flex: 1, padding: '14px', borderRadius: 12, border: 'none',
                background: 'rgba(255,255,255,0.12)', color: 'white',
                fontSize: 16, fontWeight: 600, cursor: idx >= total - 1 ? 'not-allowed' : 'pointer',
                opacity: idx >= total - 1 ? 0.4 : 1,
              }}
            >
              Přeskočit →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Desktop pack card ────────────────────────────────────────────────────────
function PackCard({ order, onPack }) {
  const [packing, setPacking] = useState(false);

  async function pack() {
    setPacking(true);
    await packingApi.pack(order.id);
    setPacking(false);
    onPack();
  }

  return (
    <div className="card" style={{
      borderLeft: `4px solid ${order.is_paid ? 'var(--green)' : 'var(--orange)'}`,
      opacity: !order.is_paid ? 0.8 : 1,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 15 }}>{order.customer_name}</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{order.order_number}</div>
        </div>
        <span className={`badge ${order.is_paid ? 'badge-paid' : 'badge-waiting'}`}>
          {order.is_paid ? '✓ Zaplaceno' : '⏳ Čeká platbu'}
        </span>
      </div>

      <div style={{ marginBottom: 12 }}>
        {order.items?.map((item, i) => (
          <div key={i} style={{ display: 'flex', padding: '4px 0', fontSize: 14, borderBottom: '1px solid var(--border-light)' }}>
            <strong style={{ minWidth: 28, color: 'var(--blue)' }}>{item.quantity}×</strong>
            <span>{item.product_name}{item.variant ? ` (${item.variant})` : ''}</span>
          </div>
        ))}
      </div>

      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 }}>
        {order.delivery_method === 'post' ? `📮 ${order.customer_address}, ${order.customer_city}` : '🏠 Vyzvednutí'}
        {order.customer_phone && <div>📱 {order.customer_phone}</div>}
      </div>

      <button
        className={`btn ${order.is_paid ? 'btn-success' : 'btn-warning'}`}
        style={{ width: '100%', padding: '10px' }}
        onClick={pack}
        disabled={packing}
      >
        {packing ? '⏳' : '📦 Zabalit'}
      </button>
    </div>
  );
}
